import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Coffee, Gift, Trash2, Settings, HandHeart, RotateCcw, Cake } from "lucide-react";

const now = new Date();
const todayMMDD = `${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export function Customers() {
  const { t } = useLanguage();
  const [customers, setCustomers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [goal, setGoal] = useState("10");
  const [rewardName, setRewardName] = useState("");

  const load = () => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/settings").then((r) => {
      setSettings(r.data);
      setGoal(String(r.data.stamps_goal));
      setRewardName(r.data.reward_name);
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    try {
      await api.put("/settings", {
        cover_price: settings.cover_price,
        stamps_goal: parseInt(goal, 10) || 10,
        reward_name: rewardName,
      });
      toast.success(t("settings_saved"));
      setSettingsOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const redeem = async (c) => {
    try {
      await api.post(`/customers/${c.user_id}/redeem`);
      toast.success(t("redeemed_marked"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const deliver = async (c) => {
    try {
      await api.post(`/customers/${c.user_id}/deliver`);
      toast.success(t("delivered_marked"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const resetRewards = async (c) => {
    try {
      await api.post(`/customers/${c.user_id}/reset-rewards`);
      toast.success(t("rewards_reset"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(t("delete_customer_confirm"))) return;
    try {
      await api.delete(`/customers/${c.user_id}`);
      toast.success(t("customer_deleted"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const goalNum = parseInt(goal, 10) || 10;

  return (
    <div data-testid="customers-list">
      <div className="flex justify-end mb-4">
        <Button
          data-testid="loyalty-settings-btn"
          variant="outline"
          size="sm"
          onClick={() => setSettingsOpen(true)}
          className="rounded-full border-[#E8DFD5] text-[#4A2C2A] gap-1.5"
        >
          <Settings className="w-4 h-4" /> {t("loyalty_settings")}
        </Button>
      </div>

      {customers.length === 0 ? (
        <p className="text-sm text-[#786C66]" data-testid="no-customers">{t("no_customers")}</p>
      ) : (
        <div className="bg-white border border-[#E8DFD5] rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E8DFD5] text-left text-xs uppercase tracking-widest text-[#786C66]">
                  <th className="p-3">{t("customer_col")}</th>
                  <th className="p-3">{t("stamps_col")}</th>
                  <th className="p-3">{t("rewards_pending_col")}</th>
                  <th className="p-3">{t("rewards_col")}</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.user_id} data-testid={`customer-row-${c.user_id}`} className="border-b border-[#F6F1E9] last:border-0">
                    <td className="p-3">
                      <p className="font-semibold text-[#1F1615] flex items-center gap-1.5">
                        {c.name}
                        {c.birthday && c.birthday === todayMMDD && <Cake data-testid={`birthday-icon-${c.user_id}`} className="w-4 h-4 text-[#D97706]" title="Compleanno oggi" />}
                      </p>
                      <p className="text-xs text-[#786C66]">{c.email}{c.birthday ? ` · 🎂 ${c.birthday.split("-")[1]}/${c.birthday.split("-")[0]}` : ""}</p>
                    </td>
                    <td className="p-3">
                      <span className="inline-flex items-center gap-1.5 font-bold text-[#4A2C2A]">
                        <Coffee className="w-4 h-4 text-[#D97706]" />
                        {c.stamps || 0}/{goalNum}
                      </span>
                      {(c.stamps || 0) >= goalNum && (
                        <Button
                          data-testid={`redeem-reward-${c.user_id}`}
                          size="sm"
                          onClick={() => redeem(c)}
                          className="ml-2 rounded-full bg-amber-500 hover:bg-amber-400 text-stone-900 text-xs h-7 gap-1 font-bold"
                        >
                          <Gift className="w-3.5 h-3.5" /> {t("redeem_reward")}
                        </Button>
                      )}
                    </td>
                    <td className="p-3">
                      <span className="font-bold text-[#1F1615]" data-testid={`pending-count-${c.user_id}`}>{c.rewards_pending || 0}</span>
                      {(c.rewards_pending || 0) > 0 && (
                        <Button
                          data-testid={`deliver-reward-${c.user_id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => deliver(c)}
                          className="ml-2 rounded-full border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-xs h-7 gap-1"
                        >
                          <HandHeart className="w-3.5 h-3.5" /> {t("deliver_reward")}
                        </Button>
                      )}
                    </td>
                    <td className="p-3 text-[#786C66]">
                      <span data-testid={`claimed-count-${c.user_id}`}>{c.rewards_claimed || 0}</span>
                      {(c.rewards_claimed || 0) > 0 && (
                        <Button
                          data-testid={`reset-rewards-${c.user_id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => resetRewards(c)}
                          className="ml-2 rounded-full border-[#E8DFD5] text-[#4A2C2A] hover:bg-[#F6F1E9] text-xs h-7 gap-1"
                        >
                          <RotateCcw className="w-3.5 h-3.5" /> {t("reset_rewards")}
                        </Button>
                      )}
                    </td>
                    <td className="p-3">
                      <Button
                        data-testid={`delete-customer-${c.user_id}`}
                        variant="ghost"
                        size="icon"
                        onClick={() => remove(c)}
                        title={t("delete_customer")}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="bg-[#FDFBF7] max-w-sm" data-testid="loyalty-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#4A2C2A]">{t("loyalty_settings")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{t("reward_name")}</Label>
              <Input data-testid="reward-name-input" value={rewardName} onChange={(e) => setRewardName(e.target.value)} className="bg-white border-[#E8DFD5]" />
            </div>
            <div>
              <Label className="text-xs">{t("stamps_goal")}</Label>
              <Input data-testid="stamps-goal-input" type="number" min="2" max="50" value={goal} onChange={(e) => setGoal(e.target.value)} className="bg-white border-[#E8DFD5]" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button data-testid="save-loyalty-settings-btn" onClick={saveSettings} className="flex-1 rounded-full bg-[#4A2C2A] hover:bg-[#36201F] text-[#FDFBF7]">{t("save")}</Button>
              <Button variant="outline" onClick={() => setSettingsOpen(false)} className="rounded-full border-[#E8DFD5]">{t("cancel")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
